import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import BackButton from '../components/BackButton';
import { 
  User, Mail, Phone, MapPin, Award, Calendar, Users, 
  CheckCircle, AlertCircle, ChevronRight, ChevronLeft,
  FileText, Shield, Heart, ClipboardCheck
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DIVISION_OPTIONS = [
  { value: 'flag_5-6', label: 'Flag Football (Ages 5-6)' },
  { value: 'flag_7-8', label: 'Flag Football (Ages 7-8)' },
  { value: 'tackle_7-8', label: 'Tackle Football (Ages 7-8)' },
  { value: 'tackle_9-10', label: 'Tackle Football (Ages 9-10)' },
  { value: 'tackle_11-12', label: 'Tackle Football (Ages 11-12)' },
  { value: 'tackle_13-14', label: 'Tackle Football (Ages 13-14)' },
  { value: 'cheer_5-8', label: 'Cheer (Ages 5-8)' },
  { value: 'cheer_9-14', label: 'Cheer (Ages 9-14)' }
];

const ROLE_OPTIONS = [
  { value: 'head_coach', label: 'Head Coach' },
  { value: 'assistant_coach', label: 'Assistant Coach' },
  { value: 'volunteer_coach', label: 'Volunteer Coach' }
];

const CoachApplicationPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [applicationId, setApplicationId] = useState(null);
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    // Personal Info
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: 'CT',
    zip_code: '',
    
    // Experience
    coaching_experience: '',
    years_experience: 0,
    previous_teams: '',
    
    // Preferences
    preferred_divisions: [],
    preferred_role: 'assistant_coach',
    availability_notes: '',
    
    // References
    reference1_name: '',
    reference1_phone: '',
    reference1_relationship: '',
    reference2_name: '',
    reference2_phone: '',
    reference2_relationship: '',
    
    // Consent
    background_check_consent: false,
    code_of_conduct_accepted: false
  });

  // Pre-fill if user is logged in
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        phone: user.phone || ''
      }));
    }
  }, [user]);

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const toggleDivision = (division) => {
    setFormData(prev => ({
      ...prev,
      preferred_divisions: prev.preferred_divisions.includes(division)
        ? prev.preferred_divisions.filter(d => d !== division)
        : [...prev.preferred_divisions, division]
    }));
  };

  const validateStep = (stepNum) => {
    const newErrors = {};
    
    if (stepNum === 1) {
      if (!formData.first_name.trim()) newErrors.first_name = 'First name is required';
      if (!formData.last_name.trim()) newErrors.last_name = 'Last name is required';
      if (!formData.email.trim()) newErrors.email = 'Email is required';
      if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    }
    
    if (stepNum === 3) {
      if (formData.preferred_divisions.length === 0) {
        newErrors.preferred_divisions = 'Please select at least one division';
      }
    }
    
    if (stepNum === 5) {
      if (!formData.background_check_consent) {
        newErrors.background_check_consent = 'You must consent to background check';
      }
      if (!formData.code_of_conduct_accepted) {
        newErrors.code_of_conduct_accepted = 'You must accept the code of conduct';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(step)) {
      setStep(prev => Math.min(prev + 1, 5));
    }
  };

  const prevStep = () => {
    setStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateStep(5)) return;
    
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/coach-applications`, formData, {
        withCredentials: true
      });
      setApplicationId(res.data.application_id);
      setSubmitted(true);
    } catch (error) {
      console.error('Failed to submit application:', error);
      alert(error.response?.data?.detail || 'Failed to submit application. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl border border-gray-200 shadow-lg max-w-lg w-full overflow-hidden">
          <div className="bg-green-500 text-white p-6 text-center">
            <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-10 h-10 text-white" />
            </div>
            <h2 className="font-display text-2xl font-black uppercase tracking-tight">Application Submitted!</h2>
          </div>
          <div className="p-6 text-center">
            <p className="text-gray-600 mb-6 font-body">
              Thank you for your interest in coaching with Park City Chiefs. 
              We'll review your application and contact you soon.
            </p>
            <div className="bg-navy/5 border border-navy/10 rounded-lg p-4 mb-6">
              <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">Application Reference</p>
              <p className="font-display text-xl font-black text-navy">{applicationId}</p>
            </div>
            <p className="text-sm text-gray-500 mb-6 font-body">
              Save this reference number to check your application status.
            </p>
            <div className="space-y-3">
              <Button 
                onClick={() => navigate('/coach-application/status')}
                className="w-full bg-navy hover:bg-navy/90 text-white font-bold uppercase tracking-wider"
              >
                Check Application Status
              </Button>
              <Button 
                variant="outline"
                onClick={() => navigate('/')}
                className="w-full border-2 hover:border-navy font-bold uppercase tracking-wider"
              >
                Return to Home
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const StepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      {[1, 2, 3, 4, 5].map((s) => (
        <div key={s} className="flex items-center">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center font-display text-sm font-black ${
            s < step ? 'bg-green-500 text-white' :
            s === step ? 'bg-navy text-white shadow-lg' :
            'bg-gray-200 text-gray-500'
          }`}>
            {s < step ? <CheckCircle className="w-5 h-5" /> : s}
          </div>
          {s < 5 && (
            <div className={`w-8 sm:w-12 h-1 mx-1 rounded-full ${s < step ? 'bg-green-500' : 'bg-gray-200'}`} />
          )}
        </div>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Back Button */}
        <div className="mb-4">
          <BackButton to="/" label="Back to Home" />
        </div>
        
        {/* Header - Athletic Style */}
        <div className="text-center mb-8">
          <img 
            src="/chiefs-logo.png" 
            alt="Park City Chiefs" 
            className="h-24 mx-auto mb-4"
            onError={(e) => e.target.style.display = 'none'}
          />
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-gold mb-2">Join Our Team</p>
          <h1 className="font-display text-3xl sm:text-4xl font-black text-navy uppercase tracking-tighter mb-2">Coach Application</h1>
          <p className="text-gray-600 font-body">Join the Park City Chiefs coaching staff</p>
        </div>

        <Card>
          <CardHeader className="pb-2">
            <StepIndicator />
            <CardTitle className="text-center text-navy">
              {step === 1 && 'Personal Information'}
              {step === 2 && 'Coaching Experience'}
              {step === 3 && 'Preferences & Availability'}
              {step === 4 && 'References'}
              {step === 5 && 'Review & Consent'}
            </CardTitle>
          </CardHeader>
          
          <CardContent className="pt-6">
            {/* Step 1: Personal Info */}
            {step === 1 && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                    <Input
                      value={formData.first_name}
                      onChange={(e) => updateField('first_name', e.target.value)}
                      placeholder="John"
                      className={errors.first_name ? 'border-red' : ''}
                    />
                    {errors.first_name && <p className="text-red text-xs mt-1">{errors.first_name}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                    <Input
                      value={formData.last_name}
                      onChange={(e) => updateField('last_name', e.target.value)}
                      placeholder="Smith"
                      className={errors.last_name ? 'border-red' : ''}
                    />
                    {errors.last_name && <p className="text-red text-xs mt-1">{errors.last_name}</p>}
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => updateField('email', e.target.value)}
                    placeholder="john.smith@email.com"
                    className={errors.email ? 'border-red' : ''}
                  />
                  {errors.email && <p className="text-red text-xs mt-1">{errors.email}</p>}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                  <Input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => updateField('phone', e.target.value)}
                    placeholder="(203) 555-0123"
                    className={errors.phone ? 'border-red' : ''}
                  />
                  {errors.phone && <p className="text-red text-xs mt-1">{errors.phone}</p>}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                  <Input
                    value={formData.address}
                    onChange={(e) => updateField('address', e.target.value)}
                    placeholder="123 Main St"
                  />
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                    <Input
                      value={formData.city}
                      onChange={(e) => updateField('city', e.target.value)}
                      placeholder="Bridgeport"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                    <Input
                      value={formData.state}
                      onChange={(e) => updateField('state', e.target.value)}
                      placeholder="CT"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">ZIP</label>
                    <Input
                      value={formData.zip_code}
                      onChange={(e) => updateField('zip_code', e.target.value)}
                      placeholder="06604"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Experience */}
            {step === 2 && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Years of Coaching Experience</label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.years_experience}
                    onChange={(e) => updateField('years_experience', parseInt(e.target.value) || 0)}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Coaching Background</label>
                  <Textarea
                    value={formData.coaching_experience}
                    onChange={(e) => updateField('coaching_experience', e.target.value)}
                    placeholder="Describe your coaching experience, including sports, age groups, and any certifications..."
                    rows={4}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Previous Teams/Organizations</label>
                  <Textarea
                    value={formData.previous_teams}
                    onChange={(e) => updateField('previous_teams', e.target.value)}
                    placeholder="List any teams or organizations you've coached with..."
                    rows={3}
                  />
                </div>
              </div>
            )}

            {/* Step 3: Preferences */}
            {step === 3 && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Preferred Age Groups/Divisions *
                  </label>
                  <p className="text-xs text-gray-500 mb-3">Select all that apply</p>
                  <div className="grid grid-cols-2 gap-2">
                    {DIVISION_OPTIONS.map(div => (
                      <button
                        key={div.value}
                        type="button"
                        onClick={() => toggleDivision(div.value)}
                        className={`p-3 rounded-lg border text-left text-sm transition-colors ${
                          formData.preferred_divisions.includes(div.value)
                            ? 'border-navy bg-navy/5 text-navy'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center">
                          <div className={`w-4 h-4 rounded border mr-2 flex items-center justify-center ${
                            formData.preferred_divisions.includes(div.value)
                              ? 'bg-navy border-navy'
                              : 'border-gray-300'
                          }`}>
                            {formData.preferred_divisions.includes(div.value) && (
                              <CheckCircle className="w-3 h-3 text-white" />
                            )}
                          </div>
                          {div.label}
                        </div>
                      </button>
                    ))}
                  </div>
                  {errors.preferred_divisions && (
                    <p className="text-red text-xs mt-2">{errors.preferred_divisions}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Role</label>
                  <div className="space-y-2">
                    {ROLE_OPTIONS.map(role => (
                      <button
                        key={role.value}
                        type="button"
                        onClick={() => updateField('preferred_role', role.value)}
                        className={`w-full p-3 rounded-lg border text-left transition-colors ${
                          formData.preferred_role === role.value
                            ? 'border-navy bg-navy/5 text-navy'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center">
                          <div className={`w-4 h-4 rounded-full border mr-3 flex items-center justify-center ${
                            formData.preferred_role === role.value
                              ? 'border-navy'
                              : 'border-gray-300'
                          }`}>
                            {formData.preferred_role === role.value && (
                              <div className="w-2 h-2 rounded-full bg-navy" />
                            )}
                          </div>
                          {role.label}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Availability Notes</label>
                  <Textarea
                    value={formData.availability_notes}
                    onChange={(e) => updateField('availability_notes', e.target.value)}
                    placeholder="Any scheduling constraints, preferred practice days/times, etc."
                    rows={3}
                  />
                </div>
              </div>
            )}

            {/* Step 4: References */}
            {step === 4 && (
              <div className="space-y-6">
                <p className="text-sm text-gray-600 mb-4">
                  Please provide two references who can speak to your character and coaching abilities.
                </p>
                
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-medium text-navy mb-3">Reference 1</h3>
                  <div className="space-y-3">
                    <Input
                      value={formData.reference1_name}
                      onChange={(e) => updateField('reference1_name', e.target.value)}
                      placeholder="Full Name"
                    />
                    <Input
                      value={formData.reference1_phone}
                      onChange={(e) => updateField('reference1_phone', e.target.value)}
                      placeholder="Phone Number"
                    />
                    <Input
                      value={formData.reference1_relationship}
                      onChange={(e) => updateField('reference1_relationship', e.target.value)}
                      placeholder="Relationship (e.g., Former colleague, Pastor)"
                    />
                  </div>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-medium text-navy mb-3">Reference 2</h3>
                  <div className="space-y-3">
                    <Input
                      value={formData.reference2_name}
                      onChange={(e) => updateField('reference2_name', e.target.value)}
                      placeholder="Full Name"
                    />
                    <Input
                      value={formData.reference2_phone}
                      onChange={(e) => updateField('reference2_phone', e.target.value)}
                      placeholder="Phone Number"
                    />
                    <Input
                      value={formData.reference2_relationship}
                      onChange={(e) => updateField('reference2_relationship', e.target.value)}
                      placeholder="Relationship"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 5: Review & Consent */}
            {step === 5 && (
              <div className="space-y-6">
                {/* Summary */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-medium text-navy mb-3">Application Summary</h3>
                  <div className="space-y-2 text-sm">
                    <p><span className="text-gray-500">Name:</span> {formData.first_name} {formData.last_name}</p>
                    <p><span className="text-gray-500">Email:</span> {formData.email}</p>
                    <p><span className="text-gray-500">Phone:</span> {formData.phone}</p>
                    <p><span className="text-gray-500">Experience:</span> {formData.years_experience} years</p>
                    <p><span className="text-gray-500">Role:</span> {ROLE_OPTIONS.find(r => r.value === formData.preferred_role)?.label}</p>
                    <div>
                      <span className="text-gray-500">Divisions:</span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {formData.preferred_divisions.map(d => (
                          <Badge key={d} variant="outline" className="text-xs">
                            {DIVISION_OPTIONS.find(opt => opt.value === d)?.label}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Required Certifications Notice */}
                <div className="bg-light-blue/10 rounded-lg p-4">
                  <h3 className="font-medium text-navy mb-2 flex items-center">
                    <Shield className="w-5 h-5 mr-2" />
                    Required Certifications
                  </h3>
                  <p className="text-sm text-gray-600 mb-3">
                    After approval, you'll need to complete the following:
                  </p>
                  <ul className="text-sm space-y-2">
                    <li className="flex items-center">
                      <CheckCircle className="w-4 h-4 text-navy mr-2" />
                      Background Check (via YMCA Partner)
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-4 h-4 text-navy mr-2" />
                      USA Football Certification
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-4 h-4 text-navy mr-2" />
                      CPR/First Aid Certification
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-4 h-4 text-navy mr-2" />
                      Concussion Training
                    </li>
                  </ul>
                </div>

                {/* Consent Checkboxes */}
                <div className="space-y-4">
                  <label className={`flex items-start space-x-3 p-4 rounded-lg border cursor-pointer ${
                    formData.background_check_consent ? 'border-green-500 bg-green-50' : 'border-gray-200'
                  } ${errors.background_check_consent ? 'border-red' : ''}`}>
                    <input
                      type="checkbox"
                      checked={formData.background_check_consent}
                      onChange={(e) => updateField('background_check_consent', e.target.checked)}
                      className="mt-1"
                    />
                    <div>
                      <p className="font-medium text-gray-900">Background Check Consent *</p>
                      <p className="text-sm text-gray-600">
                        I consent to a background check as required by Park City Chiefs and understand this is mandatory for all coaching staff.
                      </p>
                    </div>
                  </label>
                  {errors.background_check_consent && (
                    <p className="text-red text-xs">{errors.background_check_consent}</p>
                  )}

                  <label className={`flex items-start space-x-3 p-4 rounded-lg border cursor-pointer ${
                    formData.code_of_conduct_accepted ? 'border-green-500 bg-green-50' : 'border-gray-200'
                  } ${errors.code_of_conduct_accepted ? 'border-red' : ''}`}>
                    <input
                      type="checkbox"
                      checked={formData.code_of_conduct_accepted}
                      onChange={(e) => updateField('code_of_conduct_accepted', e.target.checked)}
                      className="mt-1"
                    />
                    <div>
                      <p className="font-medium text-gray-900">Code of Conduct *</p>
                      <p className="text-sm text-gray-600">
                        I have read and agree to abide by the Park City Chiefs Coach Code of Conduct, including maintaining a positive environment for all players.
                      </p>
                    </div>
                  </label>
                  {errors.code_of_conduct_accepted && (
                    <p className="text-red text-xs">{errors.code_of_conduct_accepted}</p>
                  )}
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8 pt-6 border-t">
              {step > 1 ? (
                <Button variant="outline" onClick={prevStep}>
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Previous
                </Button>
              ) : (
                <Button variant="outline" onClick={() => navigate('/')}>
                  Cancel
                </Button>
              )}
              
              {step < 5 ? (
                <Button onClick={nextStep} className="bg-navy hover:bg-navy/90">
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button 
                  onClick={handleSubmit} 
                  disabled={loading}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {loading ? 'Submitting...' : 'Submit Application'}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Already have an application? */}
        <div className="text-center mt-6">
          <p className="text-gray-600 text-sm">
            Already submitted an application?{' '}
            <button 
              onClick={() => navigate('/coach-application/status')}
              className="text-navy font-medium hover:underline"
            >
              Check your status
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default CoachApplicationPage;
